/**
 * Trunnion AI - Mission Column Component
 * Single Kanban column representing a mission on the mission board.
 * Displays a colored header with mission name and workflow count,
 * a scrollable list of draggable workflow cards, and a drop zone
 * that highlights with a gold border when items are dragged over.
 */

import { useState, type DragEvent } from 'react';
import { Plus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { MissionWorkflowCard, type WorkflowExecutionStatus } from './MissionWorkflowCard';
import type { ClassificationLevel } from '@/types';
import type { DragItem } from '@/hooks/useDragAndDrop';

/** Color indicator variants for mission columns. */
export type MissionColor = 'gold' | 'blue' | 'green' | 'slate';

/** A workflow assigned to a mission, used for rendering cards. */
export interface MissionWorkflow {
  /** Unique workflow identifier */
  id: string;
  /** Display name of the workflow */
  name: string;
  /** Classification level */
  classification: ClassificationLevel;
  /** Last execution status */
  executionStatus: WorkflowExecutionStatus;
  /** Number of agents in this workflow */
  agentCount: number;
}

/** Props for the MissionColumn component. */
interface MissionColumnProps {
  /** Unique mission identifier */
  id: string;
  /** Mission display name */
  name: string;
  /** Color theme for the column header indicator */
  color: MissionColor;
  /** Workflows assigned to this mission */
  workflows: MissionWorkflow[];
  /** Handler for drag start on a card within this column */
  onDragStart: (e: DragEvent<HTMLElement>, item: DragItem) => void;
  /** Handler for drag over this column (drop zone) */
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  /** Handler for dropping a card onto this column */
  onDrop: (e: DragEvent<HTMLElement>, targetColumn: string) => void;
  /** Handler for drag end on a card within this column */
  onDragEnd: () => void;
  /** The item currently being dragged (if any), to dim the source card */
  draggedItemId: string | null;
  /** Callback when "Add Workflow" is clicked */
  onAddWorkflow?: (missionId: string) => void;
}

/** Maps mission color variants to Tailwind CSS classes for the header indicator bar. */
const colorIndicatorClasses: Record<MissionColor, string> = {
  gold: 'bg-trunnion-gold-500',
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  slate: 'bg-trunnion-slate-500',
};

/**
 * Single Kanban column on the mission board.
 *
 * Features:
 * - Colored header indicator bar matching mission theme
 * - Mission name and workflow count badge
 * - Scrollable list of MissionWorkflowCards
 * - Drop zone highlighting with gold border during drag-over
 * - "Add Workflow" button at bottom
 * - Empty state when no workflows are assigned
 */
export function MissionColumn({
  id,
  name,
  color,
  workflows,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  draggedItemId,
  onAddWorkflow,
}: MissionColumnProps) {
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  /**
   * Handles dragOver events for this column.
   * Sets visual highlight state and delegates to the parent handler.
   */
  const handleDragOver = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setIsDraggedOver(true);
    onDragOver(e);
  };

  /** Removes highlight when drag leaves this column. */
  const handleDragLeave = (e: DragEvent<HTMLElement>) => {
    // Only clear highlight when leaving the column entirely,
    // not when entering a child element within the column
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggedOver(false);
  };

  /** Handles the drop event, clears highlight, and delegates to parent. */
  const handleDrop = (e: DragEvent<HTMLElement>) => {
    setIsDraggedOver(false);
    onDrop(e, id);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`flex w-80 shrink-0 flex-col rounded-xl border bg-trunnion-slate-900/50 transition-colors ${
        isDraggedOver
          ? 'border-trunnion-gold-500 shadow-lg shadow-trunnion-gold-500/10'
          : 'border-trunnion-slate-700'
      }`}
    >
      {/* Column header */}
      <div className="flex items-center gap-3 border-b border-trunnion-slate-700 px-4 py-3">
        {/* Color indicator */}
        <div className={`h-3 w-3 rounded-full ${colorIndicatorClasses[color]}`} />

        {/* Mission name */}
        <h3 className="text-sm font-semibold text-white truncate flex-1">{name}</h3>

        {/* Workflow count badge */}
        <Badge variant="default">{workflows.length}</Badge>
      </div>

      {/* Scrollable card list */}
      <div className="flex-1 space-y-2 overflow-y-auto p-3" style={{ maxHeight: 'calc(100vh - 220px)' }}>
        {workflows.length === 0 ? (
          <EmptyState
            message="No workflows"
            description="Drag workflows here or add a new one"
          />
        ) : (
          workflows.map((workflow) => (
            <MissionWorkflowCard
              key={workflow.id}
              id={workflow.id}
              name={workflow.name}
              classification={workflow.classification}
              executionStatus={workflow.executionStatus}
              agentCount={workflow.agentCount}
              missionId={id}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              isBeingDragged={draggedItemId === workflow.id}
            />
          ))
        )}
      </div>

      {/* Add Workflow button */}
      <div className="border-t border-trunnion-slate-700 p-3">
        <Button
          variant="ghost"
          size="sm"
          icon={<Plus className="h-4 w-4" />}
          className="w-full justify-center text-trunnion-slate-400 hover:text-trunnion-gold-400"
          onClick={() => onAddWorkflow?.(id)}
        >
          Add Workflow
        </Button>
      </div>
    </div>
  );
}
